import { Icon, Link } from '@chakra-ui/react'
import { Badge } from '@apex/component-library'
import { capitalize, isEmpty, uniqueId } from 'lodash'
import React from 'react'
import { FaArrowCircleRight } from 'react-icons/fa'
import { Link as ReactRouterLink } from 'react-router-dom'
import { useAccountSearch } from '../../hooks'
import { Account } from '../../types'
import { AccountTypeEnum } from '../../types/AccountTypeEnum'
import { Address, PhoneNumberDisplayMapping } from '../../types/CommonTypes'
import { RoutesEnum } from '../../types/RoutesEnum'
import { formatPhoneNumber } from '../../utils'
import {
  GridTable,
  GridTableColumn,
  GridTableRecord,
} from '@apex/component-library'

const ACCOUNT_STATUS_OPTIONS = [
  {
    value: 'true',
    label: 'Open',
  },
  {
    value: 'false',
    label: 'Closed',
  },
]
const ACCOUNT_TYPE_OPTIONS_EXCEPT_SHORT = [
  { value: AccountTypeEnum.Cash, label: 'Cash' },
  { value: AccountTypeEnum.Dvp, label: 'DVP' },
  { value: AccountTypeEnum.Margin, label: 'Margin' },
]
const EMPTY_ADDRESS: Address = {
  streetAddress: [''],
  city: '',
  postalCode: '',
  state: '',
  country: '',
}

const assembleColumn = (
  label: string,
  recordField: string,
  width: number,
  options?: {
    value: string
    label: string
  }[],
  cellRenderer?: ({ value }: { value: any }) => JSX.Element
): GridTableColumn => ({
  key: recordField,
  label,
  recordField,
  isFilterable: true,
  isSortable: true,
  isVisibleByDefault: true,
  width,
  ...(!isEmpty(options) && { options }),
  ...(cellRenderer && { cellRenderer }),
})

export const AccountStatusCell = ({ value }: { value: any }) => {
  const isActive = JSON.parse(value.toLowerCase())
  return (
    <Badge
      variant="solid"
      padding="0.25rem 0.75rem"
      colorScheme={isActive ? 'green' : 'red'}
    >
      {isActive ? 'Open' : 'Closed'}
    </Badge>
  )
}

export const AccountTypeCell = ({ value: accountType }: { value: any }) => (
  <span>
    {accountType !== AccountTypeEnum.Dvp
      ? capitalize(accountType)
      : accountType}
  </span>
)

export const NameAddressCell = ({ value }: { value: any }) => {
  return value.map((line: string, index: number) => {
    return <div key={index}>{line}</div>
  })
}
export const PhoneNumbersCell = ({ value }: { value: any }) => {
  return value.map((line: string, index: number) => {
    return <div key={index}>{line}</div>
  })
}

export const InitiateAccountTransferCell = ({
  value: accountNumber,
}: {
  value: any
}) => {
  const {
    accountSearchState: { searchTerm = '' },
  } = useAccountSearch()

  return (
    <Link
      as={ReactRouterLink}
      color="apex.link"
      to={{
        pathname: `/${RoutesEnum.InitiateTransfer}/${accountNumber}`,
        search: `${searchTerm ? `?q=${encodeURIComponent(searchTerm)}` : ''}`,
      }}
    >
      <Icon as={FaArrowCircleRight} /> Initiate Account Transfer
    </Link>
  )
}

export interface AccountsTableProps {
  accountResults: Account[]
  height: number
  width: number
}

const AccountsTable = ({
  accountResults,
  height,
  width,
}: AccountsTableProps) => {
  const columns: GridTableColumn[] = [
    assembleColumn(
      'Account Status',
      'isActive',
      130,
      ACCOUNT_STATUS_OPTIONS,
      AccountStatusCell
    ),
    assembleColumn('Account #', 'accountNumber', 125),
    assembleColumn('Title', 'accountTitle', 200),
    assembleColumn(
      'Name and Address',
      'nameAndAddress',
      250,
      undefined,
      NameAddressCell
    ),
    assembleColumn(
      'Phone Numbers',
      'phoneNumbers',
      200,
      undefined,
      PhoneNumbersCell
    ),
    assembleColumn('Rep Code', 'repCode', 125),
    assembleColumn('Tax ID', 'last4TIN', 150),
    assembleColumn(
      'Type',
      'accountType',
      125,
      ACCOUNT_TYPE_OPTIONS_EXCEPT_SHORT,
      AccountTypeCell
    ),
    {
      key: 'accountNumberForLink',
      label: 'Actions',
      recordField: 'accountNumberForLink',
      isFilterable: false,
      isSortable: false,
      isVisibleByDefault: true,
      width: 225,
      cellRenderer: InitiateAccountTransferCell,
    },
  ]

  const records: GridTableRecord[] = accountResults.map(
    ({
      isActive,
      accountNumber,
      accountTitle = '',
      accountNames = [],
      accountAddress = EMPTY_ADDRESS,
      phoneNumbers = [],
      repCode = '',
      last4TIN,
      accountType = '',
    }) => {
      const names = accountNames.join(' ')
      const formattedAddress = [
        ...accountAddress.streetAddress,
        `${accountAddress.city}, ${accountAddress.state || ''} ${
          accountAddress.postalCode || ''
        } ${accountAddress.country || ''}`,
      ]
      const nameAndAddress = [names].concat(formattedAddress)

      return {
        key: accountNumber,
        id: uniqueId(),
        isActive: `${isActive === true}`,
        accountNumber,
        accountTitle,
        nameAndAddress,
        phoneNumbers: phoneNumbers.map(
          ({ phoneNumberType, phoneNumber, extension }) =>
            `${PhoneNumberDisplayMapping[phoneNumberType]}: ${formatPhoneNumber(
              phoneNumber
            )} ${extension ? `x${extension}` : ''}`
        ),
        repCode,
        last4TIN: last4TIN ? `xxx-xx-${last4TIN}` : '',
        accountType,
        accountNumberForLink: accountNumber,
      }
    }
  )

  return (
    <GridTable
      title="Results"
      columns={columns}
      records={records}
      height={height}
      width={width}
    />
  )
}

export default AccountsTable
